//
// Copyright (c) 2025 Comcast
//

import { api } from './api'
import { fetchSessionUser } from 'api/sessionUser'

const enhancedApi = api.enhanceEndpoints({
  addTagTypes: [
    'partnerTicketingSystem',
    'ticketingSystem',
  ],
})

const extendedApi = enhancedApi.injectEndpoints({
  endpoints: (builder) => ({
    // PARTNER TICKETING SYSTEMS (FOR ADMINS)

    fetchPartnerTicketingSystems: builder.query({
      query: ({ partnerId }) => {
        return ({
          url: `/admin/partners/${partnerId}/ticketing_systems`,
        })
      },
      providesTags: (result) => [{ type: 'partnerTicketingSystem', id: 'LIST' }],
    }),

    createTicketingSystem: builder.mutation({
      query: ({ partnerId, params }) => {
        return ({
          url: `/admin/partners/${partnerId}/ticketing_systems`,
          method: 'POST',
          body: params,
        })
      },
      invalidatesTags: ['partnerTicketingSystem'],
    }),

    updateTicketingSystemPosition: builder.mutation({
      query: ({ partnerId, ticketingSystemDisplayId, params }) => {
        return ({
          url: `/admin/partners/${partnerId}/ticketing_systems/${ticketingSystemDisplayId}/sort`,
          method: 'PATCH',
          body: params,
        })
      },
      invalidatesTags: ['partnerTicketingSystem'],
    }),

    removeTicketingSystem: builder.mutation({
      query: ({ partnerId, ticketingSystemDisplayId }) => {
        return ({
          url: `/admin/partners/${partnerId}/ticketing_systems/${ticketingSystemDisplayId}`,
          method: 'DELETE',
        })
      },
      invalidatesTags: ['partnerTicketingSystem'],
    }),

    updateTicketingSystem: builder.mutation({
      query: ({ partnerId, ticketingSystemDisplayId, params, justification }) => {
        return ({
          url: `/admin/partners/${partnerId}/ticketing_systems/${ticketingSystemDisplayId}`,
          method: 'PATCH',
          body: {
            ticketing_system: params,
            ...(!!justification && { audit_comment: justification }),
          },
        })
      },
      // Refetch sessionUser to get ticketing systems with updated display ids
      // Simple invalidation by tag doesn't work here because fetchSessionUser
      // was not executed yet
      async onQueryStarted (arg, { queryFulfilled, dispatch, getState }) {
        await queryFulfilled

        if (arg.params?.displayId) {
          // without "forceRefetch" the request did not start and there was an error in Redux log (not in console):
          // "Aborted due to condition callback returning false."
          dispatch(fetchSessionUser.initiate(undefined, { forceRefetch: true }))
        }
      },
      invalidatesTags: ['partnerTicketingSystem', 'ticketingSystem'],
    }),

    importTicketingSystem: builder.mutation({
      query: ({ partnerId, ticketingSystemConfig }) => {
        return ({
          url: `/admin/partners/${partnerId}/ticketing_systems/import`,
          method: 'POST',
          body: ticketingSystemConfig,
        })
      },
      extraOptions: { isFileUpload: true },
      invalidatesTags: ['partnerTicketingSystem'],
    }),


    // PUBLIC TICKETING SYSTEMS

    fetchTicketingSystems: builder.query({
      query: () => '/ticketing_systems',
      providesTags: (result) => [{ type: 'ticketingSystem', id: 'LIST' }],
    }),
  }),
  overrideExisting: false,
})

export const {
  useFetchPartnerTicketingSystemsQuery,
  useCreateTicketingSystemMutation,
  useUpdateTicketingSystemPositionMutation,
  useRemoveTicketingSystemMutation,
  useUpdateTicketingSystemMutation,
  useImportTicketingSystemMutation,
  useFetchTicketingSystemsQuery,
} = extendedApi

export const {
  fetchTicketingSystems,
} = extendedApi.endpoints
